<template>
    <div class="fedex-eula-acceptance">
        <b-overlay :show="loading" rounded="sm">
            <!-- Top-level error surface. Lives outside the per-state branches so load-failure
                 modes are actually visible to the user (earlier nesting hid these inside a
                 branch that required `status` to be populated). -->
            <b-alert v-if="errorMessage"
                     show
                     variant="danger"
                     class="mb-3"
                     dismissible
                     @dismissed="errorMessage = null">
                {{ errorMessage }}
            </b-alert>

            <!-- Loaded but empty — something went wrong without a thrown error. -->
            <b-alert v-if="!status && !loading && !errorMessage" show variant="warning" class="mb-0">
                Unable to load FedEx EULA status for this store. Try refreshing the page.
            </b-alert>

            <!-- Already accepted at current version: show the audit line. -->
            <div v-if="status && status.hasCurrentAcceptance" class="accepted-summary">
                <div class="mb-2">
                    <i class="fa fa-check-circle text-success mr-1"></i>
                    <strong>FedEx EULA accepted</strong>
                </div>
                <div class="text-muted">
                    Accepted by <strong>{{ status.latestAcceptedByUserName }}</strong>
                    on {{ formatDate(status.latestAcceptedAt) }}
                    (version <code>{{ status.latestAcceptedVersion }}</code>)
                </div>
            </div>

            <!-- Accepted at an older version: show re-acceptance prompt. -->
            <b-alert v-else-if="status && status.latestAcceptedVersion"
                     show
                     variant="warning"
                     class="mb-3">
                <strong>EULA version changed.</strong>
                Previously accepted version <code>{{ status.latestAcceptedVersion }}</code>
                on {{ formatDate(status.latestAcceptedAt) }}.
                Please re-read and re-accept the current version
                (<code>{{ status && status.currentVersion }}</code>) to continue.
            </b-alert>

            <!-- Show the EULA text + acceptance form when not yet accepted at current version. -->
            <div v-if="status && !status.hasCurrentAcceptance" class="acceptance-form mt-2">
                <div class="eula-tools d-flex justify-content-end align-items-center mb-2">
                    <b-button size="sm"
                              variant="link"
                              class="p-0 mr-3"
                              @click="showFullText = true">
                        <i class="fa fa-expand mr-1"></i>Read full screen
                    </b-button>
                    <b-button size="sm"
                              variant="outline-secondary"
                              :disabled="generatingPdf"
                              @click="downloadPdf">
                        <b-spinner v-if="generatingPdf" small class="mr-1" />
                        <i v-else class="fa fa-file-pdf-o mr-1"></i>Download PDF
                    </b-button>
                </div>

                <div ref="eulaScroll"
                     class="eula-text-container glass-pane p-3 mb-3"
                     @scroll="onEulaScroll">
                    <div class="eula-version-line text-muted mb-2">
                        Version <code>{{ status.currentVersion }}</code>
                    </div>
                    <pre class="eula-text">{{ status.eulaText }}</pre>
                </div>

                <!-- AGD: "Require the End User to scroll through the EULA before being permitted
                     to accept the EULA". Checkbox stays disabled until the user scrolls to the
                     bottom of the text container. -->
                <b-form-checkbox v-model="accepted"
                                 :disabled="submitting || !scrolledToBottom">
                    I have read and agree to the FedEx Direct EULA above (version
                    <code>{{ status.currentVersion }}</code>).
                </b-form-checkbox>
                <div v-if="!scrolledToBottom" class="small text-muted mt-1">
                    <i class="fa fa-arrow-down mr-1"></i>
                    Scroll to the bottom of the EULA to enable the checkbox.
                </div>

                <div class="mt-3">
                    <!-- AGD: button text is verbatim. -->
                    <b-button variant="primary"
                              :disabled="!accepted || submitting"
                              @click="submit">
                        <b-spinner v-if="submitting" small class="mr-1" />
                        I accept the terms of FedEx EULA to start shipping
                    </b-button>
                </div>

                <b-modal v-model="showFullText"
                         size="xl"
                         scrollable
                         ok-only
                         ok-title="Close"
                         :title="`FedEx Direct EULA (version ${status.currentVersion})`">
                    <pre class="eula-text eula-text-modal">{{ status.eulaText }}</pre>
                </b-modal>
            </div>
        </b-overlay>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { FedExEulaService } from "@/services/FedExEulaService";
import { FedExEulaStatusResponse } from "@/models/FedEx/FedExEula";

@Component({
    name: "FedExEulaAcceptance"
})
export default class FedExEulaAcceptance extends Vue {
    @Prop({ required: true }) protected storeId!: number;

    protected status: FedExEulaStatusResponse | null = null;
    protected accepted: boolean = false;
    protected loading: boolean = false;
    protected submitting: boolean = false;
    protected errorMessage: string | null = null;
    protected scrolledToBottom: boolean = false;
    protected showFullText: boolean = false;
    protected generatingPdf: boolean = false;

    async created() {
        await this.load();
    }

    @Watch("storeId")
    async onStoreIdChange() {
        this.accepted = false;
        this.errorMessage = null;
        this.scrolledToBottom = false;
        this.showFullText = false;
        await this.load();
    }

    /** AGD compliance: only allow acceptance once the user has reached the end of the EULA text. */
    onEulaScroll(event: Event) {
        const el = event.target as HTMLElement;
        if (!el) return;
        // 4px fudge for sub-pixel rounding across browsers.
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 4) {
            this.scrolledToBottom = true;
        }
    }

    async load() {
        if (!this.storeId) {
            this.status = null;
            return;
        }
        this.loading = true;
        try {
            this.status = await FedExEulaService.getStatus(this.storeId);
        } catch (err: any) {
            this.errorMessage = err?.response?.data ?? "Failed to load FedEx EULA status.";
        } finally {
            this.loading = false;
        }
        // After paint, check if the EULA container already fits without scrolling — if so there's
        // nothing for the user to scroll and we treat that as "read" so the checkbox isn't
        // permanently disabled.
        this.$nextTick(() => {
            const el = this.$refs.eulaScroll as HTMLElement | undefined;
            if (el && el.scrollHeight <= el.clientHeight + 4) {
                this.scrolledToBottom = true;
            }
        });
    }

    async submit() {
        if (!this.status || !this.accepted) return;
        this.submitting = true;
        this.errorMessage = null;
        try {
            await FedExEulaService.accept(this.storeId, {
                version: this.status.currentVersion,
                accepted: true,
            });
            // Re-load to show the new "Accepted" summary.
            await this.load();
            this.accepted = false;
            this.$emit("accepted");
        } catch (err: any) {
            this.errorMessage = err?.response?.data ?? "Failed to record EULA acceptance.";
        } finally {
            this.submitting = false;
        }
    }

    async downloadPdf() {
        if (!this.status || !this.status.eulaText) return;
        this.generatingPdf = true;
        this.errorMessage = null;
        try {
            const jspdf = await import(/* webpackChunkName: "jspdf" */ "jspdf");
            const doc = new jspdf.default({ orientation: "portrait", unit: "pt", format: "letter" });
            const version = this.status.currentVersion;
            const marginX = 54;
            const marginTop = 54;
            const marginBottom = 54;
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const lineHeight = 12;
            const bottomLimit = pageHeight - marginBottom;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.text("FedEx Direct End User License Agreement", marginX, marginTop);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.text(`Version ${version}`, marginX, marginTop + 14);

            doc.setFontSize(9.5);
            // splitTextToSize measures against the CURRENT font size, so it must be set first.
            const lines: string[] = doc.splitTextToSize(this.status.eulaText, pageWidth - marginX * 2);
            let y = marginTop + 38;
            for (const line of lines) {
                if (y + lineHeight > bottomLimit) {
                    doc.addPage();
                    y = marginTop;
                }
                doc.text(line, marginX, y);
                y += lineHeight;
            }

            const pageCount = doc.getNumberOfPages();
            doc.setFontSize(8);
            for (let page = 1; page <= pageCount; page++) {
                doc.setPage(page);
                doc.text(`FedEx EULA version ${version} - Page ${page} of ${pageCount}`, marginX, pageHeight - 28);
            }

            const safeVersion = version.replace(/[^\w.-]+/g, "_");
            doc.save(`FedEx-EULA-${safeVersion}.pdf`);
        } catch (err: any) {
            console.error("FedEx EULA PDF generation failed", err);
            this.errorMessage = "Failed to generate the FedEx EULA PDF.";
        } finally {
            this.generatingPdf = false;
        }
    }

    formatDate(iso: string | null): string {
        if (!iso) return "";
        const d = new Date(iso);
        return d.toLocaleString();
    }
}
</script>

<style scoped>
.eula-text-container {
    max-height: 300px;
    overflow-y: auto;
    background-color: var(--bg-subtle, #f8f9fa);
}

.eula-text {
    white-space: pre-wrap;
    font-family: inherit;
    font-size: 0.9rem;
    margin: 0;
}

.eula-text-modal {
    max-width: 46rem;
    margin: 0 auto;
    font-size: 0.95rem;
    line-height: 1.55;
}

.eula-version-line code {
    font-size: 0.85rem;
}
</style>
